import React from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Briefcase, MapPin, Clock, ArrowRight, Users, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { seoData, translations } from '../data/translations';
import { useLanguage } from '../context/LanguageContext';
import { useCmsList } from '../hooks/useCms';

// Golden Glow Line Component
const GoldenGlowLine = () => (
  <div className="relative h-[2px] bg-black overflow-hidden">
    <div 
      className="absolute top-0 h-full w-[300px]"
      style={{
        background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
        animation: 'glowLine 4s ease-in-out infinite',
        boxShadow: '0 0 20px #D4AF37, 0 0 40px #D4AF37',
      }}
    />
    <style>{`
      @keyframes glowLine {
        0% { left: -300px; opacity: 0; }
        15% { opacity: 1; }
        85% { opacity: 1; }
        100% { left: 100%; opacity: 0; }
      }
    `}</style>
  </div>
);

// Animated Corner Frame Component
const AnimatedCornerFrame = ({ children, className = "" }) => (
  <div className={`group relative ${className}`}>
    {/* Top-left corner frame */}
    <div className="absolute top-0 left-0 w-1/2 h-1/2 overflow-hidden rounded-tl-2xl pointer-events-none">
      <div className="absolute top-0 left-0 w-[2px] h-full bg-black">
        <div 
          className="absolute left-0 w-full h-[60px]"
          style={{
            background: 'linear-gradient(180deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
            animation: 'cornerGlowVertical 4s ease-in-out infinite',
            boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
          }}
        />
      </div>
      <div className="absolute top-0 left-0 h-[2px] w-full bg-black">
        <div 
          className="absolute top-0 h-full w-[60px]"
          style={{
            background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
            animation: 'cornerGlowHorizontal 4s ease-in-out infinite',
            boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
          }}
        />
      </div>
    </div>
    
    {/* Bottom-right corner frame */}
    <div className="absolute bottom-0 right-0 w-1/2 h-1/2 overflow-hidden rounded-br-2xl pointer-events-none">
      <div className="absolute bottom-0 right-0 w-[2px] h-full bg-black">
        <div 
          className="absolute right-0 w-full h-[60px]"
          style={{
            background: 'linear-gradient(0deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
            animation: 'cornerGlowVerticalReverse 4s ease-in-out infinite',
            boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
          }}
        />
      </div>
      <div className="absolute bottom-0 right-0 h-[2px] w-full bg-black">
        <div 
          className="absolute bottom-0 h-full w-[60px]"
          style={{
            background: 'linear-gradient(270deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
            animation: 'cornerGlowHorizontalReverse 4s ease-in-out infinite',
            boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
          }}
        />
      </div>
    </div>
    {children}
    <style>{`
      @keyframes cornerGlowVertical {
        0% { top: -40px; opacity: 0; }
        15% { opacity: 1; }
        85% { opacity: 1; }
        100% { top: 100%; opacity: 0; }
      }
      @keyframes cornerGlowHorizontal {
        0% { left: -40px; opacity: 0; }
        15% { opacity: 1; }
        85% { opacity: 1; }
        100% { left: 100%; opacity: 0; }
      }
      @keyframes cornerGlowVerticalReverse {
        0% { bottom: -40px; opacity: 0; }
        15% { opacity: 1; }
        85% { opacity: 1; }
        100% { bottom: 100%; opacity: 0; }
      }
      @keyframes cornerGlowHorizontalReverse {
        0% { right: -40px; opacity: 0; }
        15% { opacity: 1; }
        85% { opacity: 1; }
        100% { right: 100%; opacity: 0; }
      }
    `}</style>
  </div>
);

const FALLBACK_JOBS = [
  {
    id: 'job-housekeeping',
    title_de: 'Housekeeping Mitarbeiter (m/w/d)',
    title_en: 'Housekeeping Staff (m/f/d)',
    type_de: 'Vollzeit / Teilzeit',
    type_en: 'Full-time / Part-time',
    location: 'Berlin',
    description_de: 'Wir suchen zuverlässige Housekeeping-Mitarbeiter für verschiedene Hotels in Berlin.',
    description_en: 'We are looking for reliable housekeeping staff for various hotels in Berlin.',
    requirements_de: ['Erfahrung im Housekeeping von Vorteil', 'Zuverlässigkeit und Sorgfalt', 'Flexibilität', 'Deutschkenntnisse'],
    requirements_en: ['Housekeeping experience preferred', 'Reliability and attention to detail', 'Flexibility', 'German language skills'],
  },
  {
    id: 'job-receptionist',
    title_de: 'Rezeptionist (m/w/d)',
    title_en: 'Receptionist (m/f/d)',
    type_de: 'Vollzeit',
    type_en: 'Full-time',
    location: 'Berlin',
    description_de: 'Für unsere Partner-Hotels suchen wir erfahrene Rezeptionisten.',
    description_en: 'We are looking for experienced receptionists for our partner hotels.',
    requirements_de: ['Erfahrung an der Hotelrezeption', 'Sehr gute Deutsch- und Englischkenntnisse', 'Freundliches Auftreten', 'Belastbarkeit'],
    requirements_en: ['Hotel reception experience', 'Excellent German and English skills', 'Friendly demeanor', 'Resilience'],
  },
  {
    id: 'job-kitchen',
    title_de: 'Küchenhilfe (m/w/d)',
    title_en: 'Kitchen Assistant (m/f/d)',
    type_de: 'Vollzeit / Teilzeit',
    type_en: 'Full-time / Part-time',
    location: 'Berlin & Brandenburg',
    description_de: 'Unterstützen Sie unsere Küchenteams in verschiedenen Hotels.',
    description_en: 'Support our kitchen teams in various hotels.',
    requirements_de: ['Erfahrung in der Gastronomie von Vorteil', 'Teamfähigkeit', 'Hygienebewusstsein', 'Bereitschaft zu Wochenendarbeit'],
    requirements_en: ['Gastronomy experience preferred', 'Team player', 'Hygiene awareness', 'Willingness to work weekends'],
  },
];

const jobT = (j) => ({
  title: { de: j.title_de, en: j.title_en },
  type: { de: j.type_de, en: j.type_en },
  description: { de: j.description_de, en: j.description_en },
  requirements: { de: j.requirements_de || [], en: j.requirements_en || [] },
});

const CareersPage = () => {
  const { t } = useLanguage();

  const jobListings = useCmsList('/api/cms/jobs', FALLBACK_JOBS);

  const benefits = [
    { de: 'Attraktive Vergütung', en: 'Attractive compensation' },
    { de: 'Flexible Arbeitszeiten', en: 'Flexible working hours' },
    { de: 'Weiterbildungsmöglichkeiten', en: 'Training opportunities' },
    { de: 'Familiäres Arbeitsumfeld', en: 'Family work environment' },
    { de: 'Langfristige Perspektiven', en: 'Long-term prospects' },
    { de: 'Verschiedene Einsatzorte', en: 'Various work locations' }
  ];
  
  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <SEO 
        title={seoData.careers.title}
        description={seoData.careers.description}
      />
      <Navbar />
      <main>
        <section className="relative pt-40 pb-20 overflow-hidden">
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-20">
              <div className="inline-flex items-center gap-3 mb-6">
                <div className="h-px w-12 bg-[#D4AF37]" />
                <span className="text-black/50 text-sm font-medium uppercase tracking-[0.3em]">Jobs</span>
                <div className="h-px w-12 bg-[#D4AF37]" />
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-black mb-6">{t('title', translations.careers)}</h1>
              <p className="text-xl text-black/60 max-w-2xl mx-auto">{t('subtitle', translations.careers)}</p>
            </div>
            <div className="mb-20">
              <div className="flex items-center gap-3 mb-8">
                <Users className="w-6 h-6 text-[#D4AF37]" />
                <h2 className="text-2xl font-bold text-black">
                  {t('whyWork', { de: 'Warum bei uns arbeiten?', en: 'Why work with us?' })}
                </h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="p-4 rounded-xl bg-white border border-black/5 hover:border-[#D4AF37]/30 transition-all duration-300 hover:shadow-lg">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mb-2" />
                    <p className="text-black/60 text-sm">{t('benefit', benefit)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        <GoldenGlowLine />
        <section className="py-20 bg-[#F9F9F9]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-8">
              <Briefcase className="w-6 h-6 text-[#D4AF37]" />
              <h2 className="text-2xl font-bold text-black">
                {t('openPositions', { de: 'Offene Stellen', en: 'Open Positions' })}
              </h2>
            </div>
            <div className="space-y-6">
              {jobListings.map((job) => (
                <AnimatedCornerFrame key={job.id} className="p-8 rounded-2xl bg-white border border-black/5 hover:shadow-xl transition-all duration-500">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-black mb-3 group-hover:text-[#D4AF37] transition-colors">
                        {t('title', jobT(job))}
                      </h3>
                      <div className="flex flex-wrap gap-4 mb-4">
                        <span className="flex items-center gap-2 text-black/60 text-sm">
                          <Clock size={14} />{t('type', jobT(job))}
                        </span>
                        <span className="flex items-center gap-2 text-black/60 text-sm">
                          <MapPin size={14} />{job.location}
                        </span>
                      </div>
                      <p className="text-black/60 mb-4">{t('description', jobT(job))}</p>
                      <div className="flex flex-wrap gap-2">
                        {t('requirements', jobT(job)).map((req, index) => (
                          <span key={index} className="px-3 py-1 rounded-full bg-[#F9F9F9] text-black/60 text-xs">
                            {req}
                          </span>
                        ))}
                      </div>
                    </div>
                    <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#003366] hover:bg-[#C9A227] rounded-lg transition-all duration-300 font-medium whitespace-nowrap">
                      <span>{t('applyNow', { de: 'Jetzt bewerben', en: 'Apply Now' })}</span>
                      <ArrowRight size={18} />
                    </Link>
                  </div>
                </AnimatedCornerFrame>
              ))}
            </div>
          </div>
        </section>
        <GoldenGlowLine />
        <section className="py-20 bg-[#F9F9F9]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <AnimatedCornerFrame className="p-12 rounded-3xl bg-white border border-black/5 shadow-xl">
              <h3 className="text-2xl font-bold text-black mb-4">
                {t('noMatch', { de: 'Keine passende Stelle gefunden?', en: "Didn't find a matching position?" })}
              </h3>
              <p className="text-black/60 mb-8 max-w-xl mx-auto">
                {t('sendInitiative', { 
                  de: 'Senden Sie uns Ihre Initiativbewerbung. Wir freuen uns, von Ihnen zu hören!',
                  en: 'Send us your unsolicited application. We look forward to hearing from you!'
                })}
              </p>
              <Link to="/contact" className="inline-flex items-center gap-3 px-8 py-4 bg-[#D4AF37] text-[#003366] font-semibold rounded-xl hover:bg-[#C9A227] hover:shadow-2xl hover:shadow-[#D4AF37]/30 hover:scale-105 transition-all duration-300">
                {t('sendApplication', { de: 'Initiativbewerbung senden', en: 'Send Application' })}
                <ArrowRight size={20} />
              </Link>
            </AnimatedCornerFrame>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CareersPage;
